import { ClerkProvider } from "@clerk/react-router";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  StrictMode,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { queryClient, usePublicPanel } from "./api";
import { ClerkAuthBoundaryCustom } from "./auth/ClerkAuthBoundary";
import {
  ClerkSignInPage,
  ClerkSignUpPage,
  SignupDetailsPage,
} from "./auth/ClerkAuthPages";
import { clerkAppearance } from "./auth/clerkTheme";
import { SsoCallbackPage } from "./auth/SsoCallback";
import { applyPortalBranding } from "./branding";
import RouteDebug from "./components/dev/RouteDebug";
import { PublicLayout } from "./components/layout/PublicLayout";
import { PageLoader } from "./components/ui/Spinner";
import "./instrument";
import Error404 from "./pages/Error404";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RedirectPage from "./pages/RedirectPage";
import { BlockedPage } from "./pages/redirects/BlockedPage";
import { CompletionPage } from "./pages/redirects/CompletionPage";
import { ErrorPage } from "./pages/redirects/ErrorPage";
import { ScreenoutPage } from "./pages/redirects/ScreenoutPage";
import Terms from "./pages/Terms";
import { PanelContext } from "./panelContext";
import "./styles/global.css";
import "./styles/LoadingSpinner.css";

function reloadOnHistoryRestore() {
  const navigationEntries = performance.getEntriesByType("navigation");
  const navigationEntry = navigationEntries[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (navigationEntry?.type === "back_forward") {
    window.location.reload();
    return;
  }

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });
}

reloadOnHistoryRestore();
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

const root = createRoot(document.getElementById("root")!, {
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler(),
});
root.render(
  <StrictMode>
    {clerkPublishableKey ? (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route element={<BrandingGate />}>
              <Route element={<PublicLayout />}>
                <Route path="redirect" element={<RedirectPage />}>
                  <Route index element={<Error404 />} />
                  <Route path=":taskId">
                    <Route index element={<Error404 />} />
                    <Route path="blocked" element={<BlockedPage />} />
                    <Route path="completion" element={<CompletionPage />} />
                    <Route path="screenout" element={<ScreenoutPage />} />
                    <Route path="error" element={<ErrorPage />} />
                    <Route path="*" element={<Error404 />} />
                  </Route>
                  <Route path="*" element={<Error404 />} />
                </Route>
              </Route>
              <Route path="error" element={<ErrorPage hideNav />} />
              <Route path="privacy" element={<PublicLayout />}>
                <Route index element={<PrivacyPolicy />} />
              </Route>
              <Route path="terms" element={<PublicLayout />}>
                <Route index element={<Terms />} />
              </Route>
              <Route
                element={<ClerkAppProvider publishableKey={clerkPublishableKey} />}
              >
                <Route path="sso" element={<SsoCallbackPage />} />
                <Route path="sign-in/*" element={<ClerkSignInPage />} />
                <Route path="sign-up/*" element={<ClerkSignUpPage />} />
                <Route path="signup/details" element={<SignupDetailsPage />} />
                <Route index element={<ClerkAuthBoundaryCustom />} />
                <Route path="*" element={<ClerkAuthBoundaryCustom />} />
              </Route>
            </Route>
            <Route path="*" element={<Error404 />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    ) : (
      <main className="auth-shell">
        <div className="auth-panel">
          <div className="auth-title title-md">Clerk is not configured</div>
          <div className="auth-copy">
            Add VITE_CLERK_PUBLISHABLE_KEY to client/.env.local and restart the
            client.
          </div>
        </div>
      </main>
    )}
  </StrictMode>,
);

function ClerkAppProvider({ publishableKey }: { publishableKey: string }) {
  return (
    <ClerkProvider
      appearance={clerkAppearance}
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
    >
      <Outlet />
    </ClerkProvider>
  );
}

function BrandingGate({ children }: { children?: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const { data, error } = usePublicPanel();
  const panelContextValue = useMemo(
    () =>
      data
        ? {
            panelId: data.id,
            panelName: data.portalBranding?.brandName || data.name,
            panelTagline:
              data.portalBranding?.brandTagline || "Your Panelist Portal",
          }
        : null,
    [data],
  );

  useEffect(() => {
    if (error) {
      setIsReady(true);
    }
  }, [error]);

  useEffect(() => {
    if (!data) return;
    applyPortalBranding(data.portalBranding, data.name || "Flex Panel");
    setIsReady(true);
  }, [data]);

  if (!isReady) return <PageLoader message="Loading..." />;
  if (!panelContextValue) return <Outlet />;
  return (
    <PanelContext.Provider value={panelContextValue}>
      <RouteDebug label="BrandingGate" />
      <Outlet />
      {children}
    </PanelContext.Provider>
  );
}
